import React, { useRef, useEffect } from 'react';
import PropTypes from 'prop-types';

import { LoadingOutlined } from '@ant-design/icons';
import { FormItem, Input, Select } from 'formik-antd';

import { simpleFilterOptions } from 'utils/form';
import { Fieldset } from 'components/form';
import countries from 'utils/countries';
import { getAddressFromPlace } from './utils';
import useAddressPredictions from './use-address-predictions';

const AddressAutoCompleteForm = ({
  prefix,
  input,
  setAddressData,
  postCodeLabel,
}) => {
  const initialValue = useRef();

  if (!initialValue.current) {
    initialValue.current = input;
  }

  const isInputValid =
    input !== '' &&
    input &&
    input.replace(/ /g, '').length >= 5 &&
    input !== initialValue.current;
  const searchInput = isInputValid ? input : '';
  const [predictions, loading] = useAddressPredictions(
    searchInput,
    undefined,
    true
  );
  const [place] = predictions;

  const addressData = place ? getAddressFromPlace(place) : undefined;

  const { country, street, town, municipality, district } = addressData || {};

  const address01 = addressData
    ? `${street}${street && district ? ', ' : ''}${district}`
    : undefined;
  const address02 = addressData ? `${municipality}` : undefined;

  useEffect(() => {
    setAddressData({
      country,
      town,
      address01,
      address02,
    });
  }, [country, town, address01, address02, setAddressData]);

  return (
    <>
      <Fieldset>
        <FormItem label={postCodeLabel} name={`${prefix}postCode`}>
          <Input
            addonAfter={loading ? <LoadingOutlined /> : null}
            name={`${prefix}postCode`}
            autoComplete="postal-code"
            placeholder="sw9 0bb"
          />
        </FormItem>
        <FormItem label="House Number or Name" name={`${prefix}houseNo`}>
          <Input name={`${prefix}houseNo`} autoComplete="address" />
        </FormItem>
      </Fieldset>

      <FormItem label="Address 1" name={`${prefix}address01`}>
        <Input name={`${prefix}address01`} autoComplete="address-line1" />
      </FormItem>

      <FormItem label="Address 2" name={`${prefix}address02`}>
        <Input name={`${prefix}address02`} autoComplete="address-line2" />
      </FormItem>

      <Fieldset>
        <FormItem label="Town" name={`${prefix}town`}>
          <Input name={`${prefix}town`} autoComplete="address-level1 " />
        </FormItem>

        <FormItem label="Country" name={`${prefix}country`}>
          <Select
            name={`${prefix}country`}
            showSearch
            allowClear
            placeholder="Select a country"
            optionFilterProp="children"
            filterOption={simpleFilterOptions}
          >
            {countries.map(value => (
              <Select.Option value={value} key={value}>
                {value}
              </Select.Option>
            ))}
          </Select>
        </FormItem>
      </Fieldset>
    </>
  );
};

AddressAutoCompleteForm.propTypes = {
  input: PropTypes.string,
  prefix: PropTypes.string,
  setAddressData: PropTypes.func.isRequired,
  postCodeLabel: PropTypes.node,
};

AddressAutoCompleteForm.defaultProps = {
  input: '',
  prefix: undefined,
  postCodeLabel: 'Post Code',
};

export default AddressAutoCompleteForm;
